import { IV3D } from '@/Interfaces/v3d';
import { Vector3, MeshBasicMaterial, BoxGeometry, Mesh, Ray } from 'three';
import { V3dTools } from '@/v3d/common';
import { boxDataProps, v3dMesh } from '@/Interfaces/common';
import { selectionBoxTarget } from '@/Engine/SelectionBox';
import { $ } from '@/Engine/stateInstance';
import { WardrobeObject } from '@/Engine/Wardrobe/WardrobeObject';
import { SelectionBoxForBox } from '@/Engine/SelectionBoxForBox';

export interface BoxProps {
    width: number;
    height: number;
    depth: number;
    uuid: string
}

const boxMaterial = new MeshBasicMaterial({ color: 0x678f99, transparent: true, opacity: 0.5 });
const geometryBox = new BoxGeometry(1,1,1);
const outlineMaterial = new MeshBasicMaterial({
    color: 0x222222,
    // side: v3d.BackSide,
    wireframe: true,
});

export class BoxItem extends WardrobeObject {
    private v3dTools: V3dTools;
    selectionBoxForBox: SelectionBoxForBox | null = null;
    _model: v3dMesh | null = null;
    _raycastMesh: v3dMesh | null = null;
    meshBoxOutline: v3dMesh | null = null;
    scale = 1.001;
    floorHeight = 0;
    public rays = {
        up: new Ray(),
        down: new Ray(),
        left: new Ray(),
        right: new Ray(),
        front: new Ray(),
        back: new Ray(),
    };
    // TODO
    // get startPoint(): xyz {
    //     return [
    //         -this.width/2,
    //         0.01,
    //         0,
    //     ];
    // }

    set position(pos: Vector3) {
        this.model.position.set(
            pos.x,
            this.height/2 + 0.01 + this.floorHeight,
            pos.z,
            );
        this.computeObb();
    }

    get position() {
        return this.model.position;
    }

    unselect() {
        if (!this.selectionBoxForBox) return;
        this.selectionBoxForBox.unselect();
        this.selectionBoxForBox = null;
    }

    select() {
        this.unselect();
        this.selectionBoxForBox = $.selectionBoxForBox;
        this.selectionBoxForBox.select(this as unknown as selectionBoxTarget);
    }

    constructor({ width, height, depth, floorHeight, uuid } : boxDataProps) {
        super({ width, height, depth, uuid, type: 'box' });
        this.v3dTools = $.v3dTools;
        // this.model = new Group();
        this.model.userData.parent = this;
        this._updateBox();
        this.floorHeight = floorHeight;
        const meshBox = new Mesh(geometryBox, boxMaterial);
        meshBox.scale.set(width, height, depth);
        this._raycastMesh = meshBox;
        this._raycastMesh.scale.set(width/this.scale, height/this.scale, depth/this.scale);
        this._raycastMesh.userData.parent = this;
        this.model.userData.side = 'floor';
        this.model.add(this._raycastMesh);
        this.meshBoxOutline = new Mesh(geometryBox, outlineMaterial);
        this.meshBoxOutline.scale.set(width, height, depth);
        this.model.add(this.meshBoxOutline);
        // this._model = new Box3Helper(this.box, new Color(0xcccc22));
        // this._model.visible = true;
        // this._model.position.set(...doorOffset);
        // this._model.userData.parent = this;
        // this.model.add(this._model);
    }

    setDimensions(width: number, height: number, depth: number, floorHeight: number) {
        this.width = width;
        this.height = height;
        this.depth = depth;
        this.floorHeight = floorHeight;
        this.model.position.set(
            this.model.position.x,
            this.height/2 + 0.01 + this.floorHeight,
            this.model.position.z,
        );
        this.meshBoxOutline.scale.set(width, height, depth);
        this._raycastMesh.scale.set(width/this.scale, height/this.scale, depth/this.scale);
        this._updateBox();
        this.computeObb();
        if ($.selectionBoxForBox.target) {
            $.selectionBoxForBox.updateGeometry();
        }
    }


    _updateBox() {
        this.box.makeEmpty();
        const dimesnions = new Vector3(this.width, this.height, this.depth);
        // const quatern = new Quaternion();
        // this.model.getWorldQuaternion(quatern);
        // dimesnions.applyQuaternion(quatern);
        dimesnions.x = Math.abs(dimesnions.x);
        dimesnions.y = Math.abs(dimesnions.y);
        dimesnions.z = Math.abs(dimesnions.z);
        // const worldPosition = new Vector3();
        // this.model.getWorldPosition(worldPosition);
        // worldPosition.y += this.height/2;
        this.box.setFromCenterAndSize(this.position, dimesnions);
    }

    computeObb() {
        // const worldQuaternion = new Quaternion();
        // this.model.getWorldQuaternion(worldQuaternion);
        const centerShift = new Vector3(0,0, 0); //.applyQuaternion(worldQuaternion);
        this.worldBox = this.box.clone();//.applyMatrix4(this.model.matrixWorld);
        const rayUpPoint = new Vector3();
        const rayDownPoint = new Vector3();
        const rayLeftPoint = new Vector3();
        const rayRightPoint = new Vector3();
        const rayBackPoint = new Vector3();
        const rayFrontPoint = new Vector3();
        const rayOrigin = new Vector3().copy(this.position);
        this.obb.fromBox3(this.box.clone());
        this.obb.center.copy(this.position);
        rayUpPoint.set(
            rayOrigin.x,
            rayOrigin.y + this.localSize.y/2,
            rayOrigin.z,
        );

        rayDownPoint.set(
            rayOrigin.x,
            rayOrigin.y - this.localSize.y/2,
            rayOrigin.z,
        );
        rayLeftPoint.set(
            -this.localSize.x/2,
            0,
            0)
            .add(rayOrigin);

        rayRightPoint.set(
            this.localSize.x/2,
            0,
            0)
            .add(rayOrigin);

        rayBackPoint.set(
            0,
            0,
            -this.localSize.z/2)
            .add(rayOrigin);

        rayFrontPoint.set(
            0,
            0,
            this.localSize.z/2)
            .add(rayOrigin);


        this.rays.up.set(rayUpPoint, new Vector3(0,1,0));
        this.rays.down.set(rayDownPoint, new Vector3(0,-1,0));
        this.rays.left.set(rayLeftPoint, new Vector3(-1,0,0));
        this.rays.right.set(rayRightPoint, new Vector3(1,0,0));
        this.rays.front.set(rayBackPoint, new Vector3(0,0,-1));
        this.rays.back.set(rayFrontPoint, new Vector3(0,0,1));

        $.app.scene.children.forEach((child)=>{
            if (child.name === 'temp' + this.uuid) $.app.scene.remove(child);
        });

        // const helper = new Mesh(new BoxGeometry(1,1,1,1,1,1), new MeshBasicMaterial({ wireframe: true }));
        // const size = new Vector3();
        // this.obb.getSize(size);
        // helper.scale.copy(size);
        // helper.position.copy(this.obb.center);
        // helper.name = 'temp'+this.uuid;
        // $.app.scene.add(helper);
    }

}
